import { createSelector } from 'reselect';
import { MetricDetail } from '@fullstory/proto/fs/services/dx-objects';
import { AppState } from '../../types';
import { AppCacheStatus } from '@fullstory/redux-util/utils';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { MetricsCacheData } from './types';
import { pickBy } from 'lodash';
import { createArrayShallowEqualSelector } from '@fullstory/redux-util/utils/selectors';

// computedMetric - same as above but using the computedMetric/non-legacy types
const getComputedMetricsDataMap = (state: AppState) => state.metrics.computedMetric;

type ComputedMetricsDataMap = ReturnType<typeof getComputedMetricsDataMap>;

export const makeComputedGetMetricsDataArray = (storageIds: string[]) =>
  createArrayShallowEqualSelector(
    createSelector(getComputedMetricsDataMap, (map: ComputedMetricsDataMap): MetricsCacheData[] =>
      storageIds.map(s => getComputedMetricsData(map, s)),
    ),
    dataArray => dataArray,
  );

export const makeComputedGetMetricsData = (storageId: string) =>
  createSelector(
    getComputedMetricsDataMap,
    (map: ComputedMetricsDataMap): MetricsCacheData => getComputedMetricsData(map, storageId),
  );

function getComputedMetricsData(map: ComputedMetricsDataMap, storageId: string) {
  const {
    status = AppCacheStatus.Initial,
    ready = false,
    data = null,
    safeMessage = null,
  } = map[storageId] ?? {};

  return { status, ready, data, storageId, safeMessage };
}

export const getSavedMetricsById = (state: AppState) => state.metrics.metrics;
export const getActiveSavedMetricsById = createSelector(getSavedMetricsById, metrics =>
  pickBy(metrics, metric => !metric.metric?.metadata?.archived),
);
export const makeGetSavedMetric = (metricId: string) =>
  createSelector(getSavedMetricsById, savedMetricsById => savedMetricsById?.[metricId]);

// Alphabetical list of metrics
export const savedMetricsList = createSelector(getSavedMetricsById, metrics => {
  const seen = new Set<string>();
  const list: MetricDetail[] = [];
  for (const key in metrics) {
    const metric = metrics[key];
    if (metric?.metric?.metric?.id && !seen.has(metric.metric.metric.id)) {
      list.push(metric);
      seen.add(metric.metric.metric.id);
    }
  }
  return list.sort(nameAndIdCompare);
});

export const activeMetricsList = createSelector(savedMetricsList, metrics =>
  metrics.filter(m => !m.metric?.metadata?.archived && !m.metric?.metric?.id?.includes('-')),
);

export const getStarredMetrics = createSelector(savedMetricsList, metrics =>
  metrics.filter(m => m.starred),
);

export const recentMetricsList = createSelector(getSavedMetricsById, metrics => {
  const list: MetricDetail[] = [];
  for (const key in metrics) {
    const metric = metrics[key];
    if (metric?.lastViewed) list.push(metric);
  }
  return list.sort((a, b) => Timestamp.toMillis(b.lastViewed) - Timestamp.toMillis(a.lastViewed));
});

export const nameAndIdCompare = (a: MetricDetail, b: MetricDetail) =>
  a.metric?.metric?.name.toLowerCase().localeCompare(b.metric?.metric?.name?.toLowerCase() ?? '') ||
  a.metric?.metric?.id.localeCompare(b.metric?.metric?.id ?? '') ||
  0;

export const getMetricsAccess = (state: AppState) => state.metrics.access;

export const getAllMetricsData = (state: AppState) => state.metrics.allMetrics;
